import emailjs from '@emailjs/browser';
import React, { useState, useEffect } from 'react';
import './ContactModal.css';

const ContactModal = ({ open, onClose, onBackdoorRequest }) => {
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        message: ''
    });

    const [isSending, setIsSending] = useState(false);

    // Start fresh when opened
    useEffect(() => {
        if (open) {
            setFormData({
                firstName: '',
                lastName: '',
                email: '',
                message: ''
            });
            setIsSending(false);
        }
    }, [open]);

    // Detect developer backdoor trigger: message === 'YYZ' and other fields blank.
    // Call `onBackdoorRequest` if provided. This allows restarting the game without
    // sending an email. We guard against repeated firing with a local flag.
    const [backdoorTriggered, setBackdoorTriggered] = useState(false);

    useEffect(() => {
        if (!open) return;
        if (backdoorTriggered) return;
        const { firstName, lastName, email, message } = formData;
        if (
            typeof message === 'string' && message.trim() === 'YYZ' &&
            (!firstName || !firstName.trim()) &&
            (!lastName || !lastName.trim()) &&
            (!email || !email.trim())
        ) {
            if (typeof onBackdoorRequest === 'function') {
                console.log('[ContactModal] Backdoor trigger detected: message=YYZ, other fields blank');
                setBackdoorTriggered(true);
                try { onBackdoorRequest(); } catch (e) { /* swallow */ }
            }
        }
    }, [formData, open, backdoorTriggered, onBackdoorRequest]);

    if (!open) return null;

    const handleChange = (e) => {
        const { name, value } = e.target;
        // Limit message to 1000 chars
        if (name === 'message' && value.length > 1000) return;

        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const isValid = () => {
        const { firstName, lastName, email, message } = formData;
        // Simple checks
        if (!firstName.trim()) return false;
        if (!lastName.trim()) return false;
        if (!email.trim() || !/\S+@\S+\.\S+/.test(email)) return false;
        if (!message.trim()) return false;
        return true;
    };

    const handleSend = () => {
        if (isSending) return;
        setIsSending(true);

        const { firstName, lastName, email, message } = formData;

        // User credentials
        const SERVICE_ID = 'service_0mduhlq';
        const TEMPLATE_ID = 'template_xn72sn8';
        const PUBLIC_KEY = 'jnkr7nsQYnO3vp7Xz';

        const templateParams = {
            name: `${firstName} ${lastName}`,
            email: email,
            message: message,
            title: 'Feedback'
        };

        emailjs.send(SERVICE_ID, TEMPLATE_ID, templateParams, PUBLIC_KEY)
            .then((response) => {
                console.log('SUCCESS!', response.status, response.text);
                alert("Message Sent! Thank you for your feedback.");
                onClose();
            })
            .catch((err) => {
                console.log('FAILED...', err);
                alert(`Failed to send message: ${err.text || 'Unknown Error'}. Please try again later.`);
                setIsSending(false);
            });
    };

    return (
        <div className="contact-modal-overlay" onClick={onClose}>
            <div className="contact-modal-content" onClick={(e) => e.stopPropagation()}>
                <button className="modal-close" onClick={onClose} aria-label="Close">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        height="24"
                        viewBox="0 0 24 24"
                        width="24"
                        fill="currentColor"
                    >
                        <path d="M0 0h24v24H0V0z" fill="none" />
                        <path d="M18.3 5.71a1 1 0 00-1.41 0L12 10.59 7.11 5.7A1 1 0 105.7 7.11L10.59 12l-4.89 4.89a1 1 0 101.41 1.41L12 13.41l4.89 4.89a1 1 0 001.41-1.41L13.41 12l4.89-4.89a1 1 0 000-1.4z" />
                    </svg>
                </button>

                <h2>Contact Us</h2>
                <p className="contact-text">
                    We hope you're enjoying Crackle, we'd love to hear from you if you have any feedback.
                </p>

                <div className="form-group">
                    <input
                        type="text"
                        name="firstName"
                        placeholder="First Name"
                        value={formData.firstName}
                        onChange={handleChange}
                    />
                </div>
                <div className="form-group">
                    <input
                        type="text"
                        name="lastName"
                        placeholder="Last Name"
                        value={formData.lastName}
                        onChange={handleChange}
                    />
                </div>
                <div className="form-group">
                    <input
                        type="email"
                        name="email"
                        placeholder="Email Address"
                        value={formData.email}
                        onChange={handleChange}
                    />
                </div>
                <div className="form-group">
                    <textarea
                        name="message"
                        placeholder="Message (1000 chars max)"
                        value={formData.message}
                        onChange={handleChange}
                        rows={4}
                    />
                    <div className="char-count">{formData.message.length}/100</div>
                </div>

                <button
                    className="send-button"
                    disabled={!isValid()}
                    onClick={handleSend}
                >
                    Send
                </button>

            </div>
        </div>
    );
};

export default ContactModal;
